/**
 * Shared shift list filter option lists (practice + professional).
 */

import type { SelectOption } from "@/types/core";

export type ShiftSpecialtyFilterOption = SelectOption;

/** Options for multi-select (empty selection = no specialty filter). */
export const buildShiftSpecialtyMultiSelectOptions = (
  specialties: Array<{ id: string; title: string }>
): ShiftSpecialtyFilterOption[] =>
  specialties.map((s) => ({ value: s.id, label: s.title }));

export type HourlyRateFilterOption = SelectOption & {
  minAmount?: number;
  maxAmount?: number;
};

export const HOURLY_RATE_FILTER_OPTIONS: HourlyRateFilterOption[] = [
  { value: "any", label: "Any Rate", minAmount: 0, maxAmount: 0 },
  { value: "0-50", label: "$0 - $50/hr", minAmount: 0, maxAmount: 50 },
  { value: "50-100", label: "$50 - $100/hr", minAmount: 50, maxAmount: 100 },
  { value: "100-150", label: "$100 - $150/hr", minAmount: 100, maxAmount: 150 },
  { value: "150+", label: "$150+/hr", minAmount: 150, maxAmount: 0 }
];

/** Rate bins without the unbounded “any” row (for FilterSelect placeholders). */
export const HOURLY_RATE_BOUNDED_FILTER_OPTIONS =
  HOURLY_RATE_FILTER_OPTIONS.filter((option) => option.value !== "any");

export const EXPERIENCE_FILTER_OPTIONS: SelectOption[] = [
  { value: "1", label: "1+ years" },
  { value: "3", label: "3+ years" },
  { value: "5", label: "5+ years" },
  { value: "10", label: "10+ years" }
];

/**
 * Maps a hourly rate to the corresponding hourly rate filter option value key.
 * Will return undefined if the rate does not fall into any known bin.
 */
export const GetHourlyRateFilterOptionValue = (hourlyRate?: number) => {
  if (!hourlyRate || typeof hourlyRate !== "number") return "any";

  if (hourlyRate < 0) return "any";
  if (hourlyRate < 50) return "0-50";
  if (hourlyRate < 100) return "50-100";
  if (hourlyRate < 150) return "100-150";
  if (hourlyRate >= 150) return "150+";

  return "any";
};

export function getHourlyRateFilterBounds(rateKey: string): {
  minHourlyRate?: number;
  maxHourlyRate?: number;
} {
  const option = HOURLY_RATE_FILTER_OPTIONS.find((item) => item.value === rateKey);
  if (!option || option.value === "any") {
    return {};
  }

  return {
    minHourlyRate: option.minAmount,
    maxHourlyRate: option.maxAmount ? option.maxAmount : undefined
  };
}